import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Popover,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DEFAULT_IMAGE_DENSITY } from '../ImageDensityWidget';
import { isDataImageUrl, isRemoteImageUrl, localizeImageUrl } from '../../utils/localizeImageUrl';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import {
  inferPositionKeyColumn,
  isHeatmapPositionColumn,
  listImageDensityPositionEntities,
  parseImageDensityPoints,
  IMAGE_DENSITY_MARKER_LIMIT,
  MARKER_VALUE_AGG_OPTIONS,
} from '../../utils/imageDensityPoints';
import { normalizeTooltipDetail, tooltipDetailChartLabel } from '../../utils/tooltipDetail';
import TooltipDetailEditorDialog from './TooltipDetailEditorDialog';

export const HEATMAP_STYLE_OPTIONS = [
  { value: 'smooth', label: 'Suave (redondeado)' },
  { value: 'sharp', label: 'Definido' },
  { value: 'bands', label: 'Bandas (niveles)' },
  { value: 'pixel', label: 'Pixelado' },
];

export const MARKER_SHAPE_OPTIONS = [
  { value: 'circle', label: 'Círculo' },
  { value: 'square', label: 'Cuadrado' },
  { value: 'pin', label: 'Pin' },
  { value: 'diamond', label: 'Rombo' },
];

export const MARKER_SIZE_OPTIONS = [
  { value: 'small', label: 'Pequeño', scale: 0.5 },
  { value: 'medium', label: 'Mediano', scale: 0.72 },
  { value: 'large', label: 'Grande', scale: 1 },
];

function MarkerShapeIcon({ shape = 'circle', scale = 1 }) {
  const size = 20;
  const r = 9 * scale;
  const c = size / 2;
  let body;
  if (shape === 'square') {
    body = <rect x={c - r} y={c - r} width={r * 2} height={r * 2} rx={r * 0.25} />;
  } else if (shape === 'diamond') {
    body = <polygon points={`${c},${c - r} ${c + r},${c} ${c},${c + r} ${c - r},${c}`} />;
  } else if (shape === 'pin') {
    const head = r * 0.58;
    const headY = c - r * 0.22;
    const tip = c + r * 0.95;
    const neck = head * 0.62;
    body = (
      <path
        d={`M ${c - neck} ${headY + head * 0.35} A ${head} ${head} 0 1 1 ${c + neck} ${headY + head * 0.35} L ${c} ${tip} Z`}
      />
    );
  } else {
    body = <circle cx={c} cy={c} r={r} />;
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="currentColor" aria-hidden="true">
      {body}
    </svg>
  );
}

function SliderRow({ label, value, display, min, max, step, onChange }) {
  return (
    <Box sx={{ px: 0.25 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}: {display}
      </Typography>
      <Slider size="small" min={min} max={max} step={step} value={value} onChange={(_, v) => onChange(v)} />
    </Box>
  );
}

function parsePoints(data, opts) {
  return parseImageDensityPoints(data, opts);
}

/**
 * Campos de configuración del heatmap sobre imagen (modo, estilo, peso, sliders, imagen…).
 * Usado en el panel «Ejes y series» del editor y en el panel «Opciones» del widget en dashboard.
 */
export default function ImageDensitySettingsFields({
  config = {},
  onConfigChange,
  data = [],
  xColumn = 'x',
  yColumn = 'y',
  getAuthHeaders,
  showImageControls = true,
  showHeader = false,
  feedLabel = 'Serie SQL',
  onClose,
  xAxisValue = '',
  yAxisValue = '',
  onXAxisChange,
  onYAxisChange,
  axisOptions = [],
  xAutoValue = '',
  yAutoValue = '',
  xAxisLabel,
  yAxisLabel,
  columnDateFormats,
  columnDateBuckets,
}) {
  const localizeImageMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/localize-image', body, { headers }),
  });

  const cfg = { ...DEFAULT_IMAGE_DENSITY, ...config };
  const {
    backgroundImageUrl = '',
    renderMode = 'heatmap',
    heatmapStyle = 'smooth',
    intensity = 1,
    overlayOpacity = 1,
    blur = 7,
    pointSize = 1,
    separation = 0.55,
    backgroundDim = 0.72,
    showLegend = true,
    groupByColumn = '',
    weightColumn = '',
    selectedGroups,
    pointRadius = 14,
    clusterEnabled = false,
    clusterRadiusPx = 25,
    clusterMerge = 'count',
    clusterShowCount = false,
    clusterColorByCount = false,
    tooltipDetail = null,
    positionMode = 'columns',
    positionKeyColumn = '',
    markerEnumColumn = '',
    markerShape = 'circle',
    markerValueColumn = '',
    markerValueAggregation = 'first',
    markerLabelColumn = '',
    markerFill = 'value',
    markerFit = 'text',
    markerSize = 'medium',
    markerColor = '#ffffff',
  } = cfg;

  const [localUrl, setLocalUrl] = useState('');
  const [urlDraft, setUrlDraft] = useState('');
  const [urlMenuAnchor, setUrlMenuAnchor] = useState(null);
  const [localizing, setLocalizing] = useState(false);
  const [localizeError, setLocalizeError] = useState('');
  const [markerColorDraft, setMarkerColorDraft] = useState('');
  const [tooltipEditorOpen, setTooltipEditorOpen] = useState(false);
  const normalizedTooltipDetail = normalizeTooltipDetail(tooltipDetail);
  const tooltipDetailConfigured = Boolean(normalizedTooltipDetail);

  useEffect(() => {
    if (isDataImageUrl(backgroundImageUrl)) {
      setUrlDraft('');
      setLocalizeError('');
    } else if (!localUrl) {
      setUrlDraft(backgroundImageUrl || '');
    }
  }, [backgroundImageUrl, localUrl]);

  useEffect(() => {
    setMarkerColorDraft(String(markerColor || '#ffffff').replace(/^#/, ''));
  }, [markerColor]);

  const patchConfig = (patch) => {
    onConfigChange?.({ ...DEFAULT_IMAGE_DENSITY, ...config, ...patch });
  };

  const numericColumns = useMemo(() => {
    if (!data?.length) return [];
    const sample = data[0] || {};
    return Object.keys(sample).filter((k) => {
      if (k === xColumn || k === yColumn || isHeatmapPositionColumn(k)) return false;
      const v = Number(sample[k]);
      return Number.isFinite(v);
    });
  }, [data, xColumn, yColumn]);

  const dimensionColumns = useMemo(() => {
    if (!data?.length) return [];
    const sample = data[0] || {};
    return Object.keys(sample).filter(
      (k) => k !== xColumn && k !== yColumn && !isHeatmapPositionColumn(k)
    );
  }, [data, xColumn, yColumn]);

  const allColumns = useMemo(() => {
    if (!data?.length) return [];
    return Object.keys(data[0] || {}).filter((k) => !isHeatmapPositionColumn(k));
  }, [data]);

  const resolvedPositionKeyColumn = useMemo(
    () => inferPositionKeyColumn(data, cfg.manualPositions, positionKeyColumn) || positionKeyColumn,
    [data, cfg.manualPositions, positionKeyColumn]
  );

  const positionKeyValues = useMemo(() => {
    if (positionMode !== 'manual' || !resolvedPositionKeyColumn) return [];
    return listImageDensityPositionEntities(data, {
      positionKeyColumn: resolvedPositionKeyColumn,
      manualPositions: cfg.manualPositions,
    }).all;
  }, [positionMode, resolvedPositionKeyColumn, data, cfg.manualPositions]);

  const allPoints = useMemo(
    () =>
      parsePoints(data, {
        xColumn,
        yColumn,
        weightColumn,
        groupByColumn,
        positionMode,
        positionKeyColumn: resolvedPositionKeyColumn,
        manualPositions: cfg.manualPositions,
      }),
    [data, xColumn, yColumn, weightColumn, groupByColumn, positionMode, resolvedPositionKeyColumn, cfg.manualPositions]
  );

  const groupOptions = useMemo(() => {
    if (!groupByColumn) return [];
    const set = new Set();
    for (const p of allPoints) {
      if (p.group) set.add(p.group);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [allPoints, groupByColumn]);

  const activeGroups = useMemo(() => {
    if (!groupByColumn || !Array.isArray(selectedGroups)) return null;
    if (selectedGroups.length === 0) return null;
    return new Set(selectedGroups.map(String));
  }, [groupByColumn, selectedGroups]);

  const toggleGroup = (group) => {
    if (!activeGroups) {
      patchConfig({ selectedGroups: [group] });
      return;
    }
    const set = new Set((selectedGroups || []).map(String));
    if (set.has(group)) set.delete(group);
    else set.add(group);
    const next = [...set];
    if (next.length === 0 || next.length === groupOptions.length) {
      patchConfig({ selectedGroups: null });
    } else {
      patchConfig({ selectedGroups: next });
    }
  };

  const handleLocalizeUrl = async (rawUrl) => {
    const trimmed = String(rawUrl || '').trim();
    if (!trimmed || isDataImageUrl(trimmed)) return false;
    if (!isRemoteImageUrl(trimmed)) {
      setLocalizeError('Usa una URL http:// o https://');
      return false;
    }
    setLocalizing(true);
    setLocalizeError('');
    try {
      const dataUrl = await localizeImageUrl(trimmed, getAuthHeaders, (body, config) =>
        localizeImageMutation.mutateAsync({ body, headers: config.headers })
      );
      setLocalUrl(dataUrl);
      setUrlDraft('');
      setUrlMenuAnchor(null);
      patchConfig({ backgroundImageUrl: dataUrl });
      return true;
    } catch (err) {
      setLocalizeError(err.response?.data?.error || err.message || 'Error al guardar imagen');
      return false;
    } finally {
      setLocalizing(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    // Sin esto, elegir de nuevo el mismo archivo no dispara onChange.
    e.target.value = '';
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      setLocalizeError('El archivo no es una imagen');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      setLocalUrl(dataUrl);
      setUrlDraft('');
      setLocalizeError('');
      patchConfig({ backgroundImageUrl: dataUrl, imageWidth: undefined, imageHeight: undefined });
    };
    reader.onerror = () => setLocalizeError('No se pudo leer el archivo');
    reader.readAsDataURL(file);
  };

  const colorScaleApplies =
    renderMode === 'heatmap' ||
    renderMode === 'points' ||
    (renderMode === 'markers' && markerFill !== 'none');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            Opciones
          </Typography>
          {onClose && (
            <IconButton size="small" onClick={onClose} aria-label="Cerrar opciones">
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {showImageControls && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" component="label" variant="outlined">
              Subir imagen
              <input type="file" accept="image/*" hidden onChange={onFile} />
            </Button>
            <Button
              size="small"
              variant="text"
              disabled={localizing}
              onClick={(e) => setUrlMenuAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}
            >
              URL
            </Button>
            {localizing && <CircularProgress size={14} />}
          </Box>
          <Popover
            open={Boolean(urlMenuAnchor)}
            anchorEl={urlMenuAnchor}
            onClose={() => {
              if (!localizing) setUrlMenuAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box sx={{ p: 1.5, width: 280, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                size="small"
                label="URL"
                autoFocus
                placeholder="https://…"
                value={isDataImageUrl(backgroundImageUrl) ? '' : urlDraft}
                disabled={localizing}
                onChange={(e) => {
                  setLocalUrl('');
                  setLocalizeError('');
                  setUrlDraft(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLocalizeUrl(urlDraft);
                  }
                }}
                fullWidth
              />
              <Button
                size="small"
                variant="contained"
                disabled={localizing || !String(urlDraft || '').trim()}
                onClick={() => handleLocalizeUrl(urlDraft)}
              >
                Usar URL
              </Button>
            </Box>
          </Popover>
          {localizeError && (
            <Typography variant="caption" color="error">
              {localizeError}
            </Typography>
          )}
          <Divider sx={{ my: 0.25 }} />
        </>
      )}

      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Posición de marcadores
      </Typography>
      <FormControl size="small" fullWidth>
        <InputLabel>Origen</InputLabel>
        <Select
          label="Origen"
          value={positionMode === 'manual' ? 'manual' : 'columns'}
          onChange={(e) => {
            const next = e.target.value === 'manual' ? 'manual' : 'columns';
            const patch = { positionMode: next };
            if (next === 'manual' && !positionKeyColumn) {
              patch.positionKeyColumn =
                inferPositionKeyColumn(data, cfg.manualPositions, '') ||
                dimensionColumns[0] ||
                allColumns[0] ||
                '';
            }
            patchConfig(patch);
          }}
        >
          <MenuItem value="manual">A mano sobre la imagen</MenuItem>
          <MenuItem value="columns">Columnas X / Y</MenuItem>
        </Select>
      </FormControl>
      {positionMode === 'manual' ? (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Columna identificador</InputLabel>
            <Select
              label="Columna identificador"
              value={resolvedPositionKeyColumn || positionKeyColumn || ''}
              onChange={(e) => patchConfig({ positionKeyColumn: e.target.value })}
            >
              <MenuItem value="">(elige una columna)</MenuItem>
              {allColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {!resolvedPositionKeyColumn
              ? 'Elige la columna cuyos valores quieres colocar en la imagen.'
              : positionKeyValues.length
                ? `${positionKeyValues.length} elementos. El lápiz del gráfico abre esta lista para colocarlos.`
                : 'La vista previa no tiene valores en esta columna.'}
          </Typography>
          {positionKeyValues.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 96, overflow: 'auto' }}>
              {[...positionKeyValues]
                .sort((a, b) => (Number(a.markerNumber) || 0) - (Number(b.markerNumber) || 0))
                .slice(0, 24)
                .map((entity) => (
                <Chip
                  key={entity.key}
                  size="small"
                  label={`${entity.markerNumber} ${entity.label}`}
                  variant={entity.placed ? 'filled' : 'outlined'}
                  title={entity.placed ? `Marcador ${entity.markerNumber}, ya tiene posición` : `Marcador ${entity.markerNumber}, sin posición`}
                />
              ))}
              {positionKeyValues.length > 24 && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  y {positionKeyValues.length - 24} más
                </Typography>
              )}
            </Box>
          )}
        </>
      ) : (
        onXAxisChange && (
          <>
            <FormControl size="small" fullWidth>
              <InputLabel>Columna X</InputLabel>
              <Select
                label="Columna X"
                value={xAxisValue || xAutoValue}
                renderValue={(v) => (xAxisLabel ? xAxisLabel(v) : v)}
                onChange={(e) => onXAxisChange(e.target.value)}
              >
                {xAutoValue ? (
                  <MenuItem value={xAutoValue}>Automático (columna «x»)</MenuItem>
                ) : null}
                {axisOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Columna Y</InputLabel>
              <Select
                label="Columna Y"
                value={yAxisValue || yAutoValue}
                renderValue={(v) => (yAxisLabel ? yAxisLabel(v) : v)}
                onChange={(e) => onYAxisChange(e.target.value)}
              >
                {yAutoValue ? (
                  <MenuItem value={yAutoValue}>Automático (columna «y»)</MenuItem>
                ) : null}
                {axisOptions.map((o) => (
                  <MenuItem key={`y-${o.value}`} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )
      )}

      <Divider sx={{ my: 0.25 }} />

      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Heatmap
      </Typography>
      <FormControl size="small" fullWidth>
        <InputLabel>Tipo de visualización</InputLabel>
        <Select
          label="Tipo de visualización"
          value={renderMode === 'points' ? 'points' : renderMode === 'markers' ? 'markers' : 'heatmap'}
          onChange={(e) => patchConfig({ renderMode: e.target.value })}
        >
          <MenuItem value="heatmap">Mapa de calor</MenuItem>
          <MenuItem value="markers">Marcadores</MenuItem>
          {renderMode === 'points' ? <MenuItem value="points">Puntos suaves</MenuItem> : null}
        </Select>
      </FormControl>

      <Divider sx={{ my: 0.25 }} />

      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Estilo
      </Typography>

      {renderMode === 'markers' ? (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Enumerar marcadores por</InputLabel>
            <Select
              label="Enumerar marcadores por"
              value={markerEnumColumn || ''}
              onChange={(e) => patchConfig({ markerEnumColumn: e.target.value })}
            >
              <MenuItem value="">(una fila, un marcador)</MenuItem>
              {allColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Un marcador por fila de la consulta.
          </Typography>
          {(data?.length || 0) > IMAGE_DENSITY_MARKER_LIMIT && (
            <Alert severity="warning" sx={{ py: 0 }}>
              Hay {data.length} filas. Solo se dibujan los primeros {IMAGE_DENSITY_MARKER_LIMIT} marcadores.
              Filtra la consulta para ver el resto.
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Forma
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={markerShape || 'circle'}
                onChange={(_, value) => {
                  if (value) patchConfig({ markerShape: value });
                }}
              >
                {MARKER_SHAPE_OPTIONS.map((o) => (
                  <ToggleButton key={o.value} value={o.value} aria-label={o.label} title={o.label} sx={{ px: 0.5, py: 0.5 }}>
                    <MarkerShapeIcon shape={o.value} />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Tamaño
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={markerSize === 'small' || markerSize === 'large' ? markerSize : 'medium'}
                onChange={(_, value) => {
                  if (value) patchConfig({ markerSize: value });
                }}
              >
                {MARKER_SIZE_OPTIONS.map((o) => (
                  <ToggleButton key={o.value} value={o.value} aria-label={o.label} title={o.label} sx={{ px: 0.5, py: 0.5 }}>
                    <MarkerShapeIcon shape={markerShape || 'circle'} scale={o.scale} />
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>Valor dentro del marcador</InputLabel>
            <Select
              label="Valor dentro del marcador"
              value={markerValueColumn || ''}
              onChange={(e) => patchConfig({ markerValueColumn: e.target.value })}
            >
              <MenuItem value="">(vacío)</MenuItem>
              {allColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {markerValueColumn ? (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Agrupación del valor</InputLabel>
                <Select
                  label="Agrupación del valor"
                  value={markerValueAggregation || 'first'}
                  onChange={(e) => patchConfig({ markerValueAggregation: e.target.value })}
                >
                  {MARKER_VALUE_AGG_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Si el marcador reúne varias filas (p. ej. una por clase), elige cómo calcular el número. La etiqueta
                externa sigue mostrando el valor de una fila (p. ej. la cámara).
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Si el valor no cabe
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={markerFit === 'marker' ? 'marker' : 'text'}
                onChange={(_, value) => {
                  if (value) patchConfig({ markerFit: value });
                }}
              >
                <ToggleButton value="text" sx={{ textTransform: 'none', px: 1.25 }}>
                  Escalar texto
                </ToggleButton>
                <ToggleButton value="marker" sx={{ textTransform: 'none', px: 1.25 }}>
                  Escalar marcador
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                {markerFit === 'marker'
                  ? 'El marcador crece para que el valor se lea cómodo y quede dentro.'
                  : 'El marcador mantiene su tamaño y la letra se achica hasta caber.'}
              </Typography>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Número o texto. Se ajusta para quedar dentro de la forma.
            </Typography>
          )}
          <FormControl size="small" fullWidth>
            <InputLabel>Etiqueta externa (opcional)</InputLabel>
            <Select
              label="Etiqueta externa (opcional)"
              value={markerLabelColumn || ''}
              onChange={(e) => patchConfig({ markerLabelColumn: e.target.value })}
            >
              <MenuItem value="">(sin etiqueta)</MenuItem>
              {allColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Se dibuja centrada debajo del marcador.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Relleno
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={markerFill === 'none' ? 'none' : 'value'}
            onChange={(_, value) => {
              if (value) patchConfig({ markerFill: value });
            }}
          >
            <ToggleButton value="value" sx={{ textTransform: 'none', px: 1.25 }}>
              Color por valor
            </ToggleButton>
            <ToggleButton value="none" sx={{ textTransform: 'none', px: 1.25 }}>
              Color fijo
            </ToggleButton>
          </ToggleButtonGroup>
          {markerFill === 'none' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="color"
                aria-label="Color de fondo del marcador"
                value={String(markerColor || '#ffffff').slice(0, 7)}
                onChange={(e) => patchConfig({ markerColor: e.target.value })}
                style={{ width: 40, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
              <TextField
                size="small"
                value={markerColorDraft}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                  setMarkerColorDraft(v);
                  if (v.length === 6) patchConfig({ markerColor: `#${v}` });
                }}
                placeholder="ffffff"
                sx={{ width: 100 }}
                inputProps={{ maxLength: 6, 'aria-label': 'Color de fondo del marcador (hexadecimal)' }}
              />
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              El color sale de la columna del valor de adentro, con la paleta de heatmap de Ajustes.
            </Typography>
          )}
        </>
      ) : renderMode === 'heatmap' ? (
        <>
          <FormControl size="small" fullWidth>
            <InputLabel>Estilo del mapa</InputLabel>
            <Select
              label="Estilo del mapa"
              value={heatmapStyle}
              onChange={(e) => patchConfig({ heatmapStyle: e.target.value })}
            >
              {HEATMAP_STYLE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Agrupar por</InputLabel>
            <Select
              label="Agrupar por"
              value={groupByColumn || ''}
              onChange={(e) => patchConfig({ groupByColumn: e.target.value, selectedGroups: null })}
            >
              <MenuItem value="">(todos juntos)</MenuItem>
              {dimensionColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Peso</InputLabel>
            <Select
              label="Peso"
              value={weightColumn || ''}
              onChange={(e) => patchConfig({ weightColumn: e.target.value })}
            >
              <MenuItem value="">(conteo)</MenuItem>
              {numericColumns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={clusterEnabled}
                onChange={(e) => patchConfig({ clusterEnabled: e.target.checked })}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                Agrupar cercanos
              </Typography>
            }
            sx={{ mx: 0, alignItems: 'center' }}
          />
          {clusterEnabled && (
            <>
              <SliderRow
                label="Radio agrupación"
                value={clusterRadiusPx}
                display={`${Math.round(clusterRadiusPx)} px`}
                min={5}
                max={120}
                step={1}
                onChange={(v) => patchConfig({ clusterRadiusPx: v })}
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Fusión</InputLabel>
                <Select
                  label="Fusión"
                  value={clusterMerge}
                  onChange={(e) => patchConfig({ clusterMerge: e.target.value })}
                >
                  <MenuItem value="count">Conteo (detecciones)</MenuItem>
                  <MenuItem value="sum">Suma del peso</MenuItem>
                  <MenuItem value="avg">Promedio del peso</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={clusterShowCount}
                    onChange={(e) => patchConfig({ clusterShowCount: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    Mostrar conteo en el centro
                  </Typography>
                }
                sx={{ mx: 0, alignItems: 'center' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={clusterColorByCount}
                    onChange={(e) => patchConfig({ clusterColorByCount: e.target.checked })}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                    Color por recurrencia (conteo)
                  </Typography>
                }
                sx={{ mx: 0, alignItems: 'center' }}
              />
            </>
          )}
          <SliderRow
            label="Intensidad"
            value={intensity}
            display={`${intensity.toFixed(2)}×`}
            min={0.4}
            max={3}
            step={0.05}
            onChange={(v) => patchConfig({ intensity: v })}
          />
          <SliderRow
            label="Opacidad capa"
            value={overlayOpacity}
            display={`${Math.round(overlayOpacity * 100)}%`}
            min={0.15}
            max={1}
            step={0.01}
            onChange={(v) => patchConfig({ overlayOpacity: v })}
          />
          <SliderRow
            label="Blur"
            value={blur}
            display={`${blur}px`}
            min={0}
            max={18}
            step={1}
            onChange={(v) => patchConfig({ blur: v })}
          />
          <SliderRow
            label="Tamaño punto"
            value={pointSize}
            display={`${pointSize.toFixed(2)}×`}
            min={0.5}
            max={2.2}
            step={0.05}
            onChange={(v) => patchConfig({ pointSize: v })}
          />
          <SliderRow
            label="Separación"
            value={separation}
            display={`${Math.round(separation * 100)}%`}
            min={0}
            max={1}
            step={0.01}
            onChange={(v) => patchConfig({ separation: v })}
          />
        </>
      ) : (
        <SliderRow
          label="Tamaño punto"
          value={pointRadius}
          display={`${pointRadius}px`}
          min={4}
          max={40}
          step={1}
          onChange={(v) => patchConfig({ pointRadius: v })}
        />
      )}

      {colorScaleApplies && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showLegend !== false}
              onChange={(e) => patchConfig({ showLegend: e.target.checked })}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
              Mostrar escala de colores
            </Typography>
          }
          sx={{ mx: 0, alignItems: 'center' }}
        />
      )}

      <SliderRow
        label="Opacidad imagen fondo"
        value={backgroundDim}
        display={`${Math.round(backgroundDim * 100)}%`}
        min={0.2}
        max={1}
        step={0.05}
        onChange={(v) => patchConfig({ backgroundDim: v })}
      />

      <Divider sx={{ my: 0.25 }} />

      <Typography variant="caption" fontWeight={600} color="text.secondary">
        Tooltip
      </Typography>
      {tooltipDetailConfigured ? (
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
          {tooltipDetailChartLabel(normalizedTooltipDetail.chart.type)} filtrado por{' '}
          <strong>{normalizedTooltipDetail.filterColumn}</strong>
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Sin tooltip: al hacer clic en un marcador no se abre nada.
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        <Button size="small" variant="outlined" onClick={() => setTooltipEditorOpen(true)} disabled={!allColumns.length}>
          {tooltipDetailConfigured ? 'Editar tooltip…' : 'Configurar tooltip…'}
        </Button>
        {tooltipDetailConfigured && (
          <Button size="small" color="inherit" onClick={() => patchConfig({ tooltipEnabled: false, tooltipDetail: null })}>
            Quitar
          </Button>
        )}
      </Box>
      {tooltipDetailConfigured && clusterEnabled && (
        <Typography variant="caption" color="text.secondary">
          En un cluster, el filtro usa los valores de todos sus puntos.
        </Typography>
      )}
      <TooltipDetailEditorDialog
        open={tooltipEditorOpen}
        onClose={() => setTooltipEditorOpen(false)}
        value={tooltipDetail}
        data={data}
        defaultFilterColumn={resolvedPositionKeyColumn || groupByColumn}
        columnDateFormats={columnDateFormats}
        columnDateBuckets={columnDateBuckets}
        onSave={(detail) => patchConfig({ tooltipEnabled: Boolean(detail), tooltipDetail: detail })}
      />

      {groupByColumn && groupOptions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
            Series
          </Typography>
          <Chip
            size="small"
            label="Todas"
            color={!activeGroups ? 'primary' : 'default'}
            variant={!activeGroups ? 'filled' : 'outlined'}
            onClick={() => patchConfig({ selectedGroups: null })}
          />
          {groupOptions.map((g) => {
            const selected = !activeGroups || activeGroups.has(g);
            return (
              <Chip
                key={g}
                size="small"
                label={g}
                color={selected && activeGroups ? 'primary' : 'default'}
                variant={selected && activeGroups ? 'filled' : 'outlined'}
                onClick={() => toggleGroup(g)}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
